import { FadeIn } from '../components/AnimateIn'
import BibleVerse from '../components/BibleVerse'
import Hero from '../components/Hero'
import Page from '../components/Page'
import Reference from '../components/Reference'
import Section from '../components/Section'
import { Text } from '../components/Text'

export default function Gospel() {
  return (
    <Page>
      <Hero title='Good News' image='Gospel'>
        <div className='flex flex-1'>
          <Text.BodyLarge>
            <p>
              The word “gospel” means “Good News.” It’s the life-changing message of who Jesus
              Christ is, what He has done for us, and its profound impact on our lives. This is the
              most important news you’ll ever hear—offering hope, salvation, and a transformed life
              through Jesus Christ.
            </p>
          </Text.BodyLarge>
        </div>
      </Hero>
      <div className='flex flex-col lg:flex-row'>
        <FadeIn delay={0.1}>
          <Section withVerticalPadding className='flex-2'>
            <div className='flex flex-col gap-24'>
              <p className='flex flex-col gap-10'>
                <Text.H2>Gospel</Text.H2>
                <Text.Body className='flex flex-col gap-6'>
                  <p>
                    Coming to faith in Jesus Christ involves a simple yet profound journey that
                    begins with acknowledging our need for His grace, believing in His sacrifice,
                    and choosing to follow Him fully. This journey can be understood in three steps:
                  </p>
                  <p>
                    A – Admit that we have sinned and ask for God’s forgiveness. B – Believe in
                    Jesus’ sacrifice on the cross and His resurrection. C – Confess and Choose to
                    let God lead our lives. These steps lead to a transformative relationship with
                    God, bringing forgiveness, purpose, and the assurance of eternal life.
                  </p>
                </Text.Body>
              </p>
              <p className='flex flex-col gap-10'>
                <Text.H2>A – Admit</Text.H2>
                <Text.Body>
                  When we acknowledge our sins and seek forgiveness, we open our hearts to God’s
                  mercy and grace. Scripture reminds us of our need for repentance and God’s
                  faithfulness in forgiving us:
                </Text.Body>
                <BibleVerse reference='Romans 3:23' url='https://www.bible.com/bible/114/ROM.3.23'>
                  For all have sinned and fall short of the glory of God.
                </BibleVerse>
                <BibleVerse reference='Romans 6:23' url='https://www.bible.com/bible/114/ROM.6.23'>
                  For the wages of sin is death, but the gift of God is eternal life in Christ Jesus
                  our Lord.
                </BibleVerse>
                <BibleVerse reference='1 John 1:9' url='https://www.bible.com/bible/114/1JN.1.9'>
                  If we confess our sins, he is faithful and just and will forgive us our sins and
                  purify us from all unrighteousness.
                </BibleVerse>
                <Text.Body>
                  Through confession, we experience God’s promise of forgiveness, embracing His
                  grace and being renewed in spirit.
                </Text.Body>
              </p>
              <p className='flex flex-col gap-10'>
                <Text.H2>B – Believe</Text.H2>
                <Text.Body>
                  To receive salvation, we must believe in the sacrifice of Jesus, who died on the
                  cross and rose again as a payment for our sins. Through His death and
                  resurrection, He offers eternal life to all who place their faith in Him. The
                  Bible affirms this truth:
                </Text.Body>
                <BibleVerse reference='John 3:16' url='https://www.bible.com/bible/114/JHN.3.16'>
                  For God so loved the world that He gave His only begotten Son, that whoever
                  believes in Him should not perish but have everlasting life.
                </BibleVerse>
                <BibleVerse
                  reference='1 Corinthians 15:3-4'
                  url='https://www.bible.com/bible/114/1CO.15.3-4'
                >
                  Christ died for our sins according to the Scriptures, and that He was buried, and
                  that He rose again the third day according to the Scriptures.
                </BibleVerse>
                <BibleVerse reference='Romans 5:8' url='https://www.bible.com/bible/114/ROM.5.8'>
                  But God demonstrates His own love for us, in that while we were still sinners,
                  Christ died for us.
                </BibleVerse>
                <Text.Body>
                  Through faith in Jesus’ redemptive work, we are assured of forgiveness, renewal,
                  and eternal life with God.
                </Text.Body>
              </p>
              <p className='flex flex-col gap-10'>
                <Text.H2>C – Confess</Text.H2>
                <Text.Body>
                  Confessing and choosing to let God lead our lives is an essential step in
                  following Jesus. This means surrendering our will, recognizing His Lordship, and
                  committing to a path of obedience and trust. The Bible guides us in this
                  commitment:
                </Text.Body>
                <BibleVerse
                  reference='Matthew 16:24'
                  url='https://www.bible.com/bible/114/MAT.16.24'
                >
                  Then said Jesus unto his disciples, If any man will come after me, let him deny
                  himself, and take up his cross, and follow me.
                </BibleVerse>
                <BibleVerse reference='Romans 10:9' url='https://www.bible.com/bible/111/ROM.10.9'>
                  If you declare with your mouth, ‘Jesus is Lord,’ and believe in your heart that
                  God raised him from the dead, you will be saved.
                </BibleVerse>
                <BibleVerse
                  reference='Romans 10:13'
                  url='https://www.bible.com/bible/116/ROM.10.13'
                >
                  Everyone who calls on the name of the Lord will be saved.
                </BibleVerse>
                <Text.Body>
                  By confessing Jesus as Lord and placing our faith in Him, we open our hearts to
                  His guidance, knowing He will lead us toward eternal life and a life transformed
                  by His love and purpose.
                </Text.Body>
              </p>
            </div>
          </Section>
        </FadeIn>
        <Section
          withVerticalPadding
          className='flex flex-1 bg-neutral-100 lg:min-w-[469px] flex-nowrap lg:px-[64px]'
        >
          <div className='flex flex-col gap-[60px]'>
            <div className='flex flex-col gap-[44px]'>
              <div className='flex flex-col gap-[44px]'>
                <FadeIn>
                  <Text.H2 className='text-nowrap flex flex-row flex-nowrap'>
                    <p>Explore Chapters</p>
                  </Text.H2>
                </FadeIn>
                <FadeIn delay={0.1}>
                  <Text.Body>
                    We encourage you to explore these chapters for a meaningful and insightful
                    reading experience.
                  </Text.Body>
                </FadeIn>
              </div>
              <div className='flex flex-col w-full gap-12'>
                <div className='flex flex-col gap-4'>
                  <FadeIn delay={0.05 * 0 + 0.2}>
                    <Text.H4>Life and Teachings of Jesus</Text.H4>
                  </FadeIn>
                  <div className='flex flex-col md:flex-row lg:flex-col md:flex-wrap lg:flex-nowrap gap-3 w-full'>
                    <Reference url='https://www.bible.com/bible/114/JHN.3.NKJV' index={1}>
                      John - Chapter 3
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/LUK.15.NKJV' index={2}>
                      Luke - Chapter 15
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/MAT.5.NKJV' index={3}>
                      Matthew - Chapters 5
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/MAT.7.NKJV' index={4}>
                      Matthew - Chapters 7
                    </Reference>
                  </div>
                </div>
                <div className='flex flex-col gap-4'>
                  <FadeIn delay={0.05 * 5 + 0.2}>
                    <Text.H4>Theology of Salvation</Text.H4>
                  </FadeIn>
                  <div className='flex flex-col md:flex-row lg:flex-col md:flex-wrap lg:flex-nowrap gap-3 w-full'>
                    <Reference url='https://www.bible.com/bible/114/ROM.3.NKJV' index={6}>
                      Romans - Chapter 3
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/ROM.5.NKJV' index={7}>
                      Romans - Chapter 5
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/ROM.8.NKJV' index={8}>
                      Romans - Chapter 8
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/EPH.2.NKJV' index={9}>
                      Ephesians - Chapter 2
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/2CO.5.NKJV' index={10}>
                      2 Corinthians - Chapter 5
                    </Reference>
                  </div>
                </div>
                <div className='flex flex-col gap-4'>
                  <FadeIn delay={0.05 * 11 + 0.2}>
                    <Text.H4>Prophecies and Foreshadowing</Text.H4>
                  </FadeIn>
                  <div className='flex flex-col md:flex-row lg:flex-col md:flex-wrap lg:flex-nowrap gap-3 w-full'>
                    <Reference url='https://www.bible.com/bible/114/ISA.53.NKJV' index={12}>
                      Isaiah - Chapter 53
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/PSA.22.NKJV' index={13}>
                      Psalms - Chapter 22
                    </Reference>
                  </div>
                </div>
                <div className='flex flex-col gap-4'>
                  <FadeIn delay={0.05 * 14 + 0.2}>
                    <Text.H4>Early Church’s Preaching</Text.H4>
                  </FadeIn>
                  <div className='flex flex-col md:flex-row lg:flex-col md:flex-wrap lg:flex-nowrap gap-3 w-full'>
                    <Reference url='https://www.bible.com/bible/114/ACT.2.NKJV' index={15}>
                      Acts - Chapter 2
                    </Reference>
                    <Reference url='https://www.bible.com/bible/114/ACT.10.NKJV' index={16}>
                      Acts - Chapter 10
                    </Reference>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </Page>
  )
}
